/* eslint-disable ember/no-computed-properties-in-native-classes */
import { computed } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismBanner,
  PrismIcon,
  PrismMetricBlock,
  PrismTooltipHeader,
} from '@mylighthouse/prism-ember';
import type { Dayjs } from 'dayjs';
import { t } from 'ember-intl';
import { and, or } from 'ember-truth-helpers';
import GeneralPercentageChangeValue from 'lumen/components/general/percentage-change-value';
import HotelPricingAssistantCalendarsDaysTooltipCompsetAndRecommendedPriceList from 'lumen/components/hotel/pricing-assistant/calendars/days/tooltip-compset-and-recommended-price-list';
import HotelPricingAssistantCalendarsDaysTooltipEventsAndHolidays from 'lumen/components/hotel/pricing-assistant/calendars/days/tooltip-events-and-holidays';
import HotelPricingAssistantSelfServiceUpsellMetricBlocksOtb from 'lumen/components/hotel/pricing-assistant/self-service-upsell-metric-blocks-otb';
import HotelPricingAssistantTooltipOtbMetricBlock from 'lumen/components/hotel/pricing-assistant/tooltip-otb-metric-block';
import dayjsFormat from 'lumen/helpers/dayjs-format';
import formatOtbValue from 'lumen/helpers/format-otb-value';
import hasFeature from 'lumen/helpers/has-feature';
import type { Carate, Carecommendation, Period } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { OpenRoomPricingFixedCategory } from 'lumen/utils/constants/pricing-assistant-constants';
import { formatAutopilotPushMessage } from 'lumen/utils/pricing-assistant/autopilot-frequency-formatting';
import { getPushErrorTranslation } from 'lumen/utils/pricing-assistant/push-error-message-translation';

import styles from './tooltip-pricing-assistant.module.css';

interface HotelPricingAssistantCalendarsDaysTooltipPricingAssistantSignature {
  Args: {
    competitorCaratesToShow: Carate[];
    date: Dayjs;
    los: number;
    maxPersons: string;
    ownHotelPricingAssistantRate: Carate;
    ownHotelRecommendation: Carecommendation;
    period: Period;
    selectedRoomCategory: OpenRoomPricingFixedCategory;
    showAutopilotState: boolean;
    showRecommendation: boolean;
  };
}

export default class HotelPricingAssistantCalendarsDaysTooltipPricingAssistant extends Component<HotelPricingAssistantCalendarsDaysTooltipPricingAssistantSignature> {
  @service declare access: Services['access'];
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  @service declare pricingAssistantPmsSs: Services['pricing-assistant-pms-ss'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get autopilotFrequencyMessageTranslation() {
    const autopilotFrequency =
      this.hotelAndMarketService.hotel.paAutopilotSettings?.settings.schedule;

    if (!autopilotFrequency) {
      return '';
    }

    return formatAutopilotPushMessage(
      Boolean(this.args.ownHotelRecommendation),
      autopilotFrequency,
      this.hotelAndMarketService,
      this.intl,
    );
  }

  @computed('args.period.day')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get day() {
    return this.args.period?.day;
  }

  @computed('args.period.day.occupancy')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get occupancy() {
    return this.args.period?.day?.occupancy;
  }

  @computed('args.period.day.predictedOccupancy')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get predictedOccupancy() {
    return this.args.period?.day?.predictedOccupancy;
  }

  get pushErrorMessage(): string {
    return getPushErrorTranslation(
      this.args.ownHotelRecommendation?.pushResultMessage,
      this.intl,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get recommendationReason() {
    if (this.args.ownHotelRecommendation) {
      return this.args.ownHotelRecommendation.paRecommendationReason;
    }

    return null;
  }

  <template>
    <PrismTooltipHeader>
      {{dayjsFormat
        @date
        format=this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG
      }}
    </PrismTooltipHeader>
    {{#if @ownHotelRecommendation.hasException}}
      <div class={{styles.exception-banner-container}}>
        <PrismBanner
          @bannerMessage={{this.pushErrorMessage}}
          @bannerStyle="critical"
          @bannerTitle={{t "pricing_assistant.exception.action_state.title"}}
          @icon="circle-minus"
          @position="top"
        />
      </div>
    {{else if @showAutopilotState}}
      <div class={{styles.autopilot-banner-container}}>
        <PrismBanner
          @bannerMessage={{this.autopilotFrequencyMessageTranslation}}
          @bannerStyle="intelligence"
          @bannerTitle={{t
            "pricing_assistant.autopilot.day_tooltip.banner.title"
          }}
          @icon="automation"
          @position="top"
        />
      </div>
    {{else if (and @showRecommendation this.recommendationReason)}}
      {{#if this.recommendationReason.formattedRecommendationReasonSummary}}
        <PrismBanner
          @bannerMessage={{this.recommendationReason.formattedRecommendationReasonSummary}}
          @icon={{this.recommendationReason.recommendationReasonSummaryTrendingIcon}}
          @position="top"
        />
      {{/if}}
    {{/if}}

    {{! Kpi section }}
    {{#if (hasFeature "pms.otb.view")}}
      <div class="{{styles.otb-container}} {{styles.section}}">
        <div
          class="{{styles.metric-blocks-container}}
            grid auto-cols-fr grid-flow-col"
        >
          <HotelPricingAssistantTooltipOtbMetricBlock
            @isCurrentOrFutureDay={{@period.isCurrentOrFutureDay}}
            @label={{t "pricing_assistant.my_OTB"}}
            @occupancyChange={{@period.occupancyChange}}
            @occupancyData={{this.occupancy}}
            @showOccupancyChange={{@period.showOccupancyChange}}
          />
          {{#if this.access.hasOTBForecastView}}
            <HotelPricingAssistantTooltipOtbMetricBlock
              @isCurrentOrFutureDay={{@period.isCurrentOrFutureDay}}
              @label={{t "pricing_assistant.my_forecast"}}
              @occupancyChange={{@period.predictedOccupancyChange}}
              @occupancyData={{this.predictedOccupancy}}
              @showOccupancyChange={{@period.showPredictedOccupancyChange}}
            />
          {{/if}}
          {{#if @period.showMarketBenchmark}}
            <PrismMetricBlock>
              <:value>
                <span>{{formatOtbValue
                    this.occupancy.benchmarkValueNormalized
                    style="percent"
                  }}</span>
                {{#if @period.showMarketBenchmarkChange}}
                  <GeneralPercentageChangeValue
                    @changeValue={{@period.benchmarkChange}}
                  />
                {{/if}}
              </:value>
              <:label>
                <div class="flex items-center justify-center gap-100">
                  {{t "rates.tooltip.market_otb"}}
                  {{#if
                    (and
                      @period.isCurrentOrFutureDay
                      this.occupancy.isMarketOtbOutOfSync
                    )
                  }}
                    <PrismIcon @name="sync-disabled" @size="extra-small" />
                  {{/if}}
                </div>
              </:label>
            </PrismMetricBlock>
          {{/if}}
        </div>
      </div>
      {{#if @period.showReferenceChangeText}}
        <p
          class="text-200--regular
            {{styles.section}}
            flex justify-center pt-300 pb-300"
        >
          {{@period.referenceChangeText}}
        </p>
      {{/if}}
    {{else if
      this.pricingAssistantPmsSs.arePricingAssistantPMSSelfServiceOTBPlaceholdersVisible
    }}
      <div class="{{styles.otb-container}} {{styles.section}}">
        <HotelPricingAssistantSelfServiceUpsellMetricBlocksOtb
          @isUpsell={{this.pricingAssistantPmsSs.showPricingAssistantPMSSelfServiceOTBPlaceholdersAsUpsellText}}
          @showTooltips={{false}}
        />
      </div>
    {{/if}}

    {{! Compset list (+ recommended price) }}
    <div
      class="compset-and-recommended-price-list-container {{styles.section}}"
    >
      <HotelPricingAssistantCalendarsDaysTooltipCompsetAndRecommendedPriceList
        @competitorCaratesToShow={{@competitorCaratesToShow}}
        @date={{@date}}
        @los={{@los}}
        @maxPersons={{@maxPersons}}
        @ownHotelPricingAssistantRate={{@ownHotelPricingAssistantRate}}
        @ownHotelRecommendation={{@ownHotelRecommendation}}
        @period={{@period}}
        @selectedRoomCategory={{@selectedRoomCategory}}
        @showAutopilotState={{@showAutopilotState}}
        @showRecommendation={{@showRecommendation}}
      />
    </div>

    {{! Events and holidays }}
    {{#if (or this.day.events.length this.day.holidays.length)}}
      <p
        class="text-200--regular {{styles.section}} pt-200 pr-300 pb-200 pl-300"
      >
        {{t "pricing_assistant.day_tooltip.events_holidays"}}
      </p>
      <div class="events-and-holidays-container {{styles.section}}">
        <HotelPricingAssistantCalendarsDaysTooltipEventsAndHolidays
          @day={{this.day}}
        />
      </div>
    {{/if}}
  </template>
}
